import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Pencil, Power, PowerOff, Trash2, RotateCw, Radio, Activity, Settings2,
  Check, Clock, RefreshCw, AlertTriangle, Wand2,
} from "lucide-react";
import { useChannel, useChannelSetup } from "@/api/queries/channels";
import {
  useEnableChannel, useDisableChannel, useDeleteChannel, useRotateChannelCredentials,
  useChannelSetupAction,
} from "@/api/mutations/channels";
import { SetupRenderer, type SetupActionPayload } from "@/components/channel-setup/SetupRenderer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { SectionCard } from "@/components/SectionCard";
import { DetailHeader, BackLink } from "@/components/DetailHeader";
import { PropGrid } from "@/components/PropGrid";
import { relativeTime } from "@/lib/relativeTime";
import { ApiError } from "@/api/client";
import { MANAGEMENT_URL } from "@/config/env";
import type { Channel } from "@/types";
import { UpdateChannelDialog } from "@/components/channel-actions/UpdateChannelDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { OneTimeTokenDialog } from "@/components/OneTimeTokenDialog";

function statusVariant(s: Channel["status"]) {
  if (s === "online") return "online" as const;
  if (s === "enrolled") return "enrolled" as const;
  if (s === "disabled") return "disabled" as const;
  return "destructive" as const;
}

// Channel-Setup-Flow: Badge-Ton pro grobem Setup-Zustand.
function setupVariant(s: string) {
  if (s === "ready") return "online" as const;
  if (s === "attention") return "destructive" as const;
  if (s === "in_progress") return "enrolled" as const;
  return "busy" as const; // needs_setup
}
const SETUP_LABELS: Record<string, string> = {
  needs_setup: "Setup nötig",
  in_progress: "Setup läuft",
  ready: "eingerichtet",
  attention: "braucht Aufmerksamkeit",
};

export function ChannelDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useChannel(id);
  // Channel-Setup-Flow: Setup-Zustand + Plugin-Schema (Poll, 3s während Setup).
  const setupQuery = useChannelSetup(id);
  const setupActionMutation = useChannelSetupAction();

  const [updateOpen, setUpdateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [rotateConfirmOpen, setRotateConfirmOpen] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [rotateResult, setRotateResult] = useState<string | null>(null);

  const enableMutation = useEnableChannel();
  const disableMutation = useDisableChannel();
  const deleteMutation = useDeleteChannel();
  const rotateMutation = useRotateChannelCredentials();

  const errorMessage =
    error instanceof ApiError && error.status === 404 ? `Channel "${id}" nicht gefunden` : (error as Error | undefined)?.message;

  function handleToggleStatus() {
    if (!data) return;
    const mut = data.status === "disabled" ? enableMutation : disableMutation;
    const label = data.status === "disabled" ? "aktiviert" : "deaktiviert";
    mut.mutate(data.id, {
      onSuccess: () => toast.success(`Channel ${label}`),
      onError: (err) => toast.error(`Toggle fehlgeschlagen: ${(err as Error).message}`),
    });
  }

  function handleDelete() {
    if (!data) return;
    setDeleteError(null);
    deleteMutation.mutate(data.id, {
      onSuccess: () => {
        toast.success(`Channel ${data.name} gelöscht`);
        setDeleteOpen(false);
        navigate("/channels");
      },
      onError: (err) => setDeleteError((err as Error).message),
    });
  }

  function handleRotate() {
    if (!data) return;
    setRotateError(null);
    rotateMutation.mutate(data.id, {
      onSuccess: ({ channelToken, natsCredentials }) => {
        // #202: komplettes ENV-Bundle — NATS (falls vorhanden) + channel-token +
        // MANAGEMENT_URL (für den Attachment-Upload/-Download des Plugins).
        const lines: string[] = [];
        if (natsCredentials) {
          lines.push(
            `NATS_URL=${natsCredentials.natsUrl}`,
            `NATS_JWT=${natsCredentials.natsJwt}`,
            `NATS_SEED=${natsCredentials.natsSeed}`,
          );
        }
        lines.push(
          `CHANNEL_ID=${data.id}`,
          `CHANNEL_TOKEN=${channelToken}`,
          `MANAGEMENT_URL=${MANAGEMENT_URL}`,
        );
        setRotateResult(lines.join("\n"));
        setRotateConfirmOpen(false);
        toast.success("Credentials rotiert (NATS + channel-token)");
      },
      onError: (err) => setRotateError((err as Error).message),
    });
  }

  const isToggling = enableMutation.isPending || disableMutation.isPending;

  // ----- Loading / Error -----
  if (isLoading && !data) {
    return (
      <SectionCard testId="detail-loading">
        <div className="flex items-center gap-3 py-8 text-sm text-text3">
          <RefreshCw className="h-4 w-4 animate-spin" /> Lädt Channel…
        </div>
      </SectionCard>
    );
  }
  if (isError || !data) {
    return (
      <div className="space-y-5">
        <BackLink to="/channels" label="Channels" />
        <SectionCard testId="detail-error">
          <div className="flex flex-col items-center gap-3 py-10 text-center">
            <AlertTriangle className="h-6 w-6 text-danger" />
            <div className="text-sm text-text2">{errorMessage ?? "Konnte nicht laden"}</div>
            <Button variant="outline" size="sm" onClick={() => refetch()}><RefreshCw className="h-4 w-4" />Retry</Button>
          </div>
        </SectionCard>
      </div>
    );
  }

  const ident = data.inventorySnapshot?.externalIdentity;
  const setup = setupQuery.data;

  function handleSetupAction(action: SetupActionPayload) {
    if (!data) return;
    setupActionMutation.mutate(
      { id: data.id, ...action },
      {
        onSuccess: () => toast.success("Aktion ans Plugin gesendet"),
        onError: (err) => toast.error(`Aktion fehlgeschlagen: ${(err as Error).message}`),
      },
    );
  }

  return (
    <div className="space-y-5">
      <DetailHeader
        backTo="/channels"
        backLabel="Channels"
        icon={Radio}
        title={data.name}
        badge={
          <Badge variant={statusVariant(data.status)}>
            {data.status === "online" && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
            {data.status}
          </Badge>
        }
        sub={<>Typ <span className="mono text-text">{data.type}</span></>}
        actions={
          <div className="flex flex-wrap gap-2" data-testid="channel-actions">
            <Button variant="outline" size="sm" onClick={() => setUpdateOpen(true)} data-testid="channel-action-update">
              <Pencil className="h-4 w-4" />Edit
            </Button>
            <Button variant="outline" size="sm" onClick={handleToggleStatus} disabled={isToggling} data-testid="channel-action-toggle-status">
              {data.status === "disabled" ? <><Power className="h-4 w-4" />Enable</> : <><PowerOff className="h-4 w-4" />Disable</>}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setRotateConfirmOpen(true)} data-testid="channel-action-rotate">
              <RotateCw className="h-4 w-4" />Rotate Credentials
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)} data-testid="channel-action-delete">
              <Trash2 className="h-4 w-4" />Delete
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 items-start gap-[18px] lg:grid-cols-[1fr_340px]">
        <div className="flex flex-col gap-[18px]">
          {/* Channel-Setup-Flow: generischer SDUI-Renderer über das Plugin-Schema */}
          <SectionCard
            icon={<Wand2 className="h-[18px] w-[18px]" />}
            title="Setup"
            description="Vom Plugin definiert — der Master reicht nur durch"
            action={
              setup?.setupState ? (
                <Badge variant={setupVariant(setup.setupState)} data-testid="channel-setup-state">
                  {SETUP_LABELS[setup.setupState] ?? setup.setupState}
                </Badge>
              ) : undefined
            }
          >
            <SetupRenderer
              schema={setup?.schema ?? null}
              onAction={handleSetupAction}
              isPending={setupActionMutation.isPending}
            />
            {/* Bestands-Channels: JWT ohne die neuen setup-Scopes → Plugin KANN nicht pushen. */}
            {!setup?.setupState && data.status === "online" && (
              <div className="mt-3 rounded-[10px] border border-info-border bg-info-bg px-3 py-2 text-[12.5px] text-info">
                Plugin läuft, meldet aber kein Setup — Channels von vor dem Setup-Flow brauchen
                einmalig „Rotate Credentials" + Container-Neustart mit dem neuen Bundle.
              </div>
            )}
          </SectionCard>

          {/* Live-Inventory (vom Plugin-Container gepusht) */}
          <SectionCard icon={<Activity className="h-[18px] w-[18px]" />} title="Live-Inventory" description="Vom Plugin-Container gepusht">
            {ident ? (
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-ok-bg text-ok">
                  <Check className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="mono text-[14px] font-semibold text-text" data-testid="channel-live-identity">{ident}</div>
                  <div className="text-[12px] text-text3">
                    {data.inventorySnapshot?.pluginStatus ? `${data.inventorySnapshot.pluginStatus} · ` : ""}
                    letzter Push {relativeTime(data.inventorySnapshot?.updatedAt ?? data.lastHeartbeatAt)}
                  </div>
                </div>
              </div>
            ) : data.inventorySnapshot ? (
              // Inventory kommt an, aber noch keine externe Identität (= noch nicht
              // gepaart). Der Container LÄUFT also — den präzisen Plugin-Zustand zeigen
              // statt des pauschalen „nicht gestartet oder nicht gepaart".
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-info-bg text-info">
                  <Clock className="h-5 w-5" />
                </span>
                <div className="min-w-0">
                  <div className="text-[13px] text-text">
                    {data.inventorySnapshot.pluginStatus === "qr_pending"
                      ? "Plugin läuft — wartet auf Pairing (QR in der Setup-Karte scannen)."
                      : `Plugin läuft — noch keine externe Identität (${data.inventorySnapshot.pluginStatus ?? "unbekannt"}).`}
                  </div>
                  <div className="text-[12px] text-text3">
                    letzter Push {relativeTime(data.inventorySnapshot.updatedAt ?? data.lastHeartbeatAt)}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-surface2 text-text3">
                  <Clock className="h-5 w-5" />
                </span>
                <div className="text-[13px] text-text3">Plugin-Container noch nicht gestartet — kein Inventory empfangen.</div>
              </div>
            )}
          </SectionCard>

          {/* Config (Operator-Hint, nicht authoritativ) */}
          <SectionCard icon={<Settings2 className="h-[18px] w-[18px]" />} title="Config" description="Operator-Hint — nicht authoritativ" noPad>
            <pre className="mono m-0 overflow-x-auto px-[18px] py-3.5 text-[12px] leading-relaxed text-text2">
              {JSON.stringify(data.config, null, 2)}
            </pre>
            {/* #298: geheime Schluessel erscheinen NIE mit Wert — nur als hinterlegt.
                Gleiches write-only-Muster wie beim OAuth-Client-Secret eines Connectors. */}
            {(data.secretConfigKeys?.length ?? 0) > 0 && (
              <div className="border-t border-border px-[18px] py-2.5" data-testid="channel-secret-keys">
                <div className="mb-1 text-[11.5px] font-semibold text-text2">Geheime Werte</div>
                <div className="flex flex-wrap gap-1.5">
                  {(data.secretConfigKeys ?? []).map((k) => (
                    <span key={k} className="mono rounded-md border border-ok-border bg-ok-bg px-1.5 py-0.5 text-[11px] text-ok-text">
                      ● {k} hinterlegt
                    </span>
                  ))}
                </div>
                <div className="mt-1.5 text-[11px] text-text3">
                  Werte sind write-only — sie verlassen den Master nicht. Zum Ersetzen im Plugin-Setup neu eintragen.
                </div>
              </div>
            )}
            <div className="border-t border-border px-[18px] py-2.5 text-[11.5px] text-text3">
              Informativ — die echte Channel-Identität kommt vom Plugin-Inventory.
            </div>
          </SectionCard>
        </div>

        <PropGrid
          rows={[
            ["ID", <span className="mono">{data.id}</span>],
            ["Typ", <Badge variant="default">{data.type}</Badge>],
            ["Status", <Badge variant={statusVariant(data.status)}>{data.status}</Badge>],
            ["Live-Identität", ident ? <span className="mono">{ident}</span> : <span className="text-text3">—</span>],
            ["Letztes Heartbeat", <span className="text-text2">{relativeTime(data.lastHeartbeatAt)}</span>],
            ["Erstellt", <span className="text-text2">{relativeTime(data.createdAt)}</span>],
          ]}
        />
      </div>

      {/* Dialoge (Logik 1:1) */}
      {updateOpen && <UpdateChannelDialog open onOpenChange={setUpdateOpen} channel={data} />}
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Channel endgültig löschen"
        description="Diese Aktion ist NICHT umkehrbar. Alle ChannelBindings (User→externe Identitäten) werden ebenfalls gelöscht."
        confirmText="Endgültig löschen"
        confirmVariant="destructive"
        isPending={deleteMutation.isPending}
        errorMessage={deleteError ?? undefined}
        requiredTypeText={data.name}
        onConfirm={handleDelete}
      />
      <ConfirmDialog
        open={rotateConfirmOpen}
        onOpenChange={setRotateConfirmOpen}
        title="Channel-Credentials rotieren"
        description="Erzeugt ein komplettes neues Bundle: NATS-User-JWT + frischer channel-token (für Attachment-Upload/-Download). Der ALTE channel-token wird sofort ungültig, das alte NATS-JWT sobald der Master die Revocations published. Der Plugin-Container muss mit dem neuen Bundle neu gestartet werden."
        confirmText="Rotieren"
        confirmVariant="destructive"
        isPending={rotateMutation.isPending}
        errorMessage={rotateError ?? undefined}
        onConfirm={handleRotate}
      />
      {rotateResult && (
        <OneTimeTokenDialog
          open
          onOpenChange={(o) => !o && setRotateResult(null)}
          title="Neues Channel-Bundle"
          description="JETZT kopieren — der channel-token ist danach nicht mehr abrufbar. In die .env des Plugin-Containers übernehmen und neu starten."
          label="ENV-Block (.env)"
          token={rotateResult}
        />
      )}
    </div>
  );
}
